'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import shallowCompare from 'react-addons-shallow-compare';
import { connect } from 'react-redux';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import ToolFormControl from 'DesignComponents/tool/ToolFormControl';
import ToolFormLabel from 'DesignComponents/tool/ToolFormLabel';
import ToolFieldset from 'DesignComponents/tool/ToolFieldset';
import UIHelpIcon from 'UIComponents/icon/UIHelpIcon';
import UISelect from 'UIComponents/input/UISelect';
import UIRadioInput from 'UIComponents/input/UIRadioInput';
import UIToggle from 'UIComponents/input/UIToggle';
import UIToggleGroup from 'UIComponents/input/UIToggleGroup';
import UICheckbox from 'UIComponents/input/UICheckbox';
import UILink from 'UIComponents/link/UILink';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import CustomWidgetField from 'DesignData/models/CustomWidgetField';
import Embed from 'ContentComponents/cmv2/fields/Embed';
import * as embedActions from 'ContentData/actions/Embed';
import * as embedSelectors from 'ContentData/selectors/embeds';
import { EMBED } from 'ContentUtils/constants/CustomWidgetFieldTypes';
import { EmbedTypes, oEmbedTypes } from 'ContentUtils/constants/MediaTypes';
import { getEmptyOEmbedValue } from 'ContentComponents/cmv2/media/mediaHelpers';
import { DEFAULT, RESIZABLE, SHOW_PREVIEW, SUPPORTED_OEMBED_TYPES, SUPPORTED_SOURCE_TYPES, SUPPORTED_MEDIA_BRIDGE_PROVIDERS } from 'DesignData/enums/CustomWidgetFieldAttributes';
import SidebarInput from 'DesignEditorsUI/components/shared/sidebar/SidebarInput';
import FormControlWithErrors from 'DesignEditorsUI/components/customWidgetEditor/sidebar/FormControlWithErrors';

const i18nKey = key => `DesignEditorsUI.customWidgetEditor.sidebar.fieldEdit.contentOptions.${key}`;

function asMutable(list) {
  if (!list) return [];
  return list.asMutable ? list.asMutable() : list;
}

const {
  HTML,
  MEDIA_BRIDGE,
  OEMBED
} = EmbedTypes;
const ALL_SOURCE_TYPES = 'ALL_SOURCE_TYPES';
const ALL_PROVIDERS = 'ALL_PROVIDERS';
const CUSTOM_PROVIDERS = 'CUSTOM_PROVIDERS';
const SUPPORTED_OEMBED_TYPES_OPTIONS = Object.keys(oEmbedTypes).map(oEmbedType => {
  return {
    text: unescapedText(i18nKey(`embed.supportedOembedTypes.options.${oEmbedType}`)),
    value: oEmbedTypes[oEmbedType]
  };
});

const mapStateToProps = state => ({
  hasFetchedProviders: embedSelectors.getHasFetchedProviders(state),
  mediaBridgeProviderOptions: embedSelectors.getMediaBridgeProviderOptions(state)
});

const mapDispatchToProps = {
  fetchMediaBridgeProviders: embedActions.fetchMediaBridgeProviders
};

class EmbedContentOptions extends Component {
  constructor(props) {
    super(props);

    this.onSupportedSourceTypeOptionToggle = e => {
      const {
        field
      } = this.props;
      const {
        checked,
        name: sourceType
      } = e.target;
      const {
        [SUPPORTED_SOURCE_TYPES]: supportedSourceTypes = []
      } = field;

      if (checked) {
        this.onSupportedSourceTypesChange(supportedSourceTypes.concat([sourceType]));
      } else {
        this.onSupportedSourceTypesChange(supportedSourceTypes.filter(_sourceType => _sourceType !== sourceType));
      }
    };

    this.onSupportedSourceTypesChange = newSourceTypes => {
      const {
        field
      } = this.props;
      const changes = {
        [SUPPORTED_SOURCE_TYPES]: newSourceTypes
      };
      const currentSourceType = field[DEFAULT] && field[DEFAULT].source_type;

      if (currentSourceType && !newSourceTypes.includes(currentSourceType)) {
        changes[DEFAULT] = Object.assign({}, field[DEFAULT], {
          source_type: newSourceTypes[0]
        });
      }

      this.props.editField(changes);
    };

    this.onSupportedOembedTypesChange = e => {
      this.props.editField({
        [SUPPORTED_OEMBED_TYPES]: e.target.value
      });
    };

    this.onSupportedMediaBridgeProvidersChange = e => {
      const {
        field,
        editField
      } = this.props;
      const {
        media_bridge_object
      } = field[DEFAULT] || {};
      const {
        provider_id
      } = media_bridge_object || {};
      const newProviders = e.target.value;
      const changes = {
        [SUPPORTED_MEDIA_BRIDGE_PROVIDERS]: newProviders
      };

      if (provider_id && !newProviders.includes(provider_id)) {
        changes[DEFAULT] = Object.assign({}, field[DEFAULT], {
          media_bridge_object: Object.assign({}, media_bridge_object, {
            id: null,
            oembed_url: '',
            provider_id: null
          }, getEmptyOEmbedValue(media_bridge_object))
        });
      }

      editField(changes);
    };

    this.onSupportedMediaBridgeProvidersCheck = e => {
      const {
        editField
      } = this.props;
      const selection = e.target;

      if (selection.name === ALL_PROVIDERS) {
        this.setState({
          isCheckedAllProviders: true
        });
        editField({
          [SUPPORTED_MEDIA_BRIDGE_PROVIDERS]: []
        });
      } else {
        this.setState({
          isCheckedAllProviders: false
        });
      }
    };

    this.onResizableChange = e => {
      this.props.editField({
        [RESIZABLE]: e.target.checked
      });
    };

    this.onShowPreviewChange = e => {
      this.props.editField({
        [SHOW_PREVIEW]: e.target.checked
      });
    };

    this.onDefaultChange = value => {
      this.props.editField({
        [DEFAULT]: value
      });
    };

    this.renderSupportedSourceTypeCheckbox = sourceType => {
      const {
        field,
        isEmail,
        readOnly
      } = this.props;
      const {
        [SUPPORTED_SOURCE_TYPES]: supportedSourceTypes = []
      } = field;
      const isHtmlInEmail = isEmail && sourceType === HTML;

      const label = /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey(`embed.supportedSourceTypes.options.${sourceType}`)
      });

      return /*#__PURE__*/_jsx(UICheckbox, {
        checked: supportedSourceTypes.includes(sourceType),
        disabled: readOnly || isHtmlInEmail,
        name: sourceType,
        onChange: this.onSupportedSourceTypeOptionToggle,
        size: "small",
        children: isHtmlInEmail ? /*#__PURE__*/_jsx(UITooltip, {
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('embed.supportedSourceTypes.emailTooltip')
          }),
          children: label
        }) : label
      }, sourceType);
    };

    const providers = props.field[SUPPORTED_MEDIA_BRIDGE_PROVIDERS];
    this.state = {
      isCheckedAllProviders: !providers || !!(providers && providers.length === 0)
    };
  }

  componentDidMount() {
    const {
      fetchMediaBridgeProviders,
      hasFetchedProviders
    } = this.props;

    if (!hasFetchedProviders) {
      fetchMediaBridgeProviders();
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState);
  }

  componentDidUpdate(prevProps) {
    const {
      fetchMediaBridgeProviders,
      hasFetchedProviders
    } = this.props;
    const {
      [SUPPORTED_SOURCE_TYPES]: prevSupportedSourceTypes
    } = prevProps.field || {};

    if (!prevSupportedSourceTypes.includes(MEDIA_BRIDGE) && this.hasMediaBridgeSource && !hasFetchedProviders) {
      fetchMediaBridgeProviders();
    }
  }

  get hasMediaBridgeSource() {
    return this.props.field[SUPPORTED_SOURCE_TYPES].includes(MEDIA_BRIDGE);
  }

  renderSupportedSourceTypesRadios() {
    const {
      field,
      fieldErrors,
      isEmail,
      mediaBridgeProviderOptions,
      readOnly
    } = this.props;

    if (mediaBridgeProviderOptions && mediaBridgeProviderOptions.length) {
      return /*#__PURE__*/_jsx(FormControlWithErrors, {
        errors: fieldErrors[SUPPORTED_SOURCE_TYPES],
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('embed.supportedSourceTypes.label')
        }),
        tooltip: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: i18nKey('embed.supportedSourceTypes.tooltip'),
          options: {
            oembedProviderLink: /*#__PURE__*/_jsx(UILink, {
              external: true,
              href: "https://oembed.com/#section7",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: i18nKey('embed.supportedSourceTypes.oembedProviderLinkText')
              })
            })
          }
        }),
        tooltipProps: {
          maxWidth: 235
        },
        children: /*#__PURE__*/_jsx(UIToggleGroup, {
          name: "supportedSourceTypes",
          children: Object.values(EmbedTypes).map(this.renderSupportedSourceTypeCheckbox)
        })
      });
    }

    let selectedSourceType = ALL_SOURCE_TYPES; // If there's a single source type, just select the corresponding radio

    if (field[SUPPORTED_SOURCE_TYPES] && field[SUPPORTED_SOURCE_TYPES].length === 1) {
      selectedSourceType = field[SUPPORTED_SOURCE_TYPES][0];
    }

    if (isEmail) selectedSourceType = EmbedTypes.OEMBED;
    return /*#__PURE__*/_jsx(ToolFormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('embed.supportedSourceTypes.label')
      }),
      tooltip: /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: i18nKey('embed.supportedSourceTypes.tooltip'),
        options: {
          oembedProviderLink: /*#__PURE__*/_jsx(UILink, {
            external: true,
            href: "https://oembed.com/#section7",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('embed.supportedSourceTypes.oembedProviderLinkText')
            })
          })
        }
      }),
      tooltipProps: {
        maxWidth: 235
      },
      children: /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(UIRadioInput, {
          checked: selectedSourceType === OEMBED,
          disabled: readOnly,
          name: OEMBED,
          onChange: () => this.onSupportedSourceTypesChange([OEMBED]),
          size: "small",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('embed.supportedSourceTypes.options.embedUrlOnly')
          })
        }), /*#__PURE__*/_jsx(UIRadioInput, {
          checked: selectedSourceType === HTML,
          disabled: readOnly || isEmail,
          name: HTML,
          onChange: () => this.onSupportedSourceTypesChange([HTML]),
          size: "small",
          children: /*#__PURE__*/_jsx(UITooltip, {
            disabled: !isEmail,
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('embed.supportedSourceTypes.emailTooltip')
            }),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('embed.supportedSourceTypes.options.embedCodeOnly')
            })
          })
        }), /*#__PURE__*/_jsx(UIRadioInput, {
          checked: selectedSourceType === ALL_SOURCE_TYPES,
          disabled: readOnly || isEmail,
          name: ALL_SOURCE_TYPES,
          onChange: () => this.onSupportedSourceTypesChange([OEMBED, HTML]),
          size: "small",
          children: /*#__PURE__*/_jsx(UITooltip, {
            disabled: !isEmail,
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('embed.supportedSourceTypes.emailTooltip')
            }),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('embed.supportedSourceTypes.options.allTypes')
            })
          })
        })]
      })
    });
  }

  renderSupportedOembedTypesSelect() {
    const {
      field,
      readOnly
    } = this.props;

    if (!field[SUPPORTED_SOURCE_TYPES] || !field[SUPPORTED_SOURCE_TYPES].includes(OEMBED)) {
      return null;
    }

    const options = field[SUPPORTED_OEMBED_TYPES] && field[SUPPORTED_OEMBED_TYPES].length === 1 ? SUPPORTED_OEMBED_TYPES_OPTIONS.map(option => {
      return Object.assign({}, option, {
        clearableValue: false
      });
    }) : SUPPORTED_OEMBED_TYPES_OPTIONS;
    return /*#__PURE__*/_jsx(ToolFormControl, {
      className: "m-top-3",
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('embed.supportedOembedTypes.label')
      }),
      tooltip: /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: i18nKey('embed.supportedOembedTypes.tooltip.message'),
        options: {
          documentationLink: /*#__PURE__*/_jsx(UILink, {
            external: true,
            href: "https://developers.hubspot.com/docs/cms/building-blocks/module-theme-fields/oembed",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('embed.supportedOembedTypes.tooltip.documentationLink')
            })
          })
        }
      }),
      children: /*#__PURE__*/_jsx(SidebarInput, {
        inputComponent: UISelect,
        disabled: readOnly,
        onChange: this.onSupportedOembedTypesChange,
        multi: true,
        options: options,
        required: true,
        value: asMutable(field[SUPPORTED_OEMBED_TYPES])
      })
    });
  }

  renderMediaBridgeProvidersOptions() {
    const {
      field,
      mediaBridgeProviderOptions,
      readOnly
    } = this.props;
    const providerList = asMutable(field[SUPPORTED_MEDIA_BRIDGE_PROVIDERS]);
    const options = [...mediaBridgeProviderOptions];

    if (!mediaBridgeProviderOptions || !mediaBridgeProviderOptions.length || !this.hasMediaBridgeSource) {
      return null;
    }

    return /*#__PURE__*/_jsx(ToolFormControl, {
      className: "m-top-3",
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('embed.supportedMediaBridgeProviders.label')
      }),
      tooltip: /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: i18nKey('embed.supportedMediaBridgeProviders.tooltip')
      }),
      children: /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(UIRadioInput, {
          checked: this.state.isCheckedAllProviders,
          disabled: readOnly,
          name: ALL_PROVIDERS,
          onChange: this.onSupportedMediaBridgeProvidersCheck,
          size: "small",
          children: /*#__PURE__*/_jsx(UIHelpIcon, {
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('embed.supportedMediaBridgeProviders.options.allProviders.tooltip')
            }),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('embed.supportedMediaBridgeProviders.options.allProviders.label')
            })
          })
        }), /*#__PURE__*/_jsx(UIRadioInput, {
          checked: !this.state.isCheckedAllProviders,
          disabled: readOnly,
          name: CUSTOM_PROVIDERS,
          onChange: this.onSupportedMediaBridgeProvidersCheck,
          size: "small",
          children: /*#__PURE__*/_jsx(UIHelpIcon, {
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('embed.supportedMediaBridgeProviders.options.custom.tooltip')
            }),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('embed.supportedMediaBridgeProviders.options.custom.label')
            })
          })
        }), !this.state.isCheckedAllProviders && /*#__PURE__*/_jsx(SidebarInput, {
          disabled: readOnly,
          inputComponent: UISelect,
          multi: true,
          onChange: this.onSupportedMediaBridgeProvidersChange,
          options: options,
          required: true,
          value: providerList
        })]
      })
    });
  }

  renderSizeControlToggle() {
    const {
      field,
      readOnly
    } = this.props;
    return /*#__PURE__*/_jsxs("div", {
      className: "m-top-6",
      children: [/*#__PURE__*/_jsx(ToolFormLabel, {
        className: "p-right-4 p-top-1",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('embed.sizeControlOptionsLabel')
        })
      }), /*#__PURE__*/_jsx(SidebarInput, {
        checked: field[RESIZABLE],
        defaultChecked: true,
        disabled: readOnly,
        inputComponent: UIToggle,
        onChange: this.onResizableChange,
        required: true,
        size: "extra-small"
      })]
    });
  }

  renderShowPreviewToggle() {
    const {
      field,
      readOnly
    } = this.props;
    return /*#__PURE__*/_jsxs("div", {
      className: "m-top-3",
      children: [/*#__PURE__*/_jsx(ToolFormLabel, {
        className: "p-right-4 p-top-1",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('embed.showPreview.labelGeneral')
        })
      }), /*#__PURE__*/_jsx(SidebarInput, {
        checked: field[SHOW_PREVIEW],
        defaultChecked: true,
        disabled: readOnly,
        inputComponent: UIToggle,
        onChange: this.onShowPreviewChange,
        required: true,
        size: "extra-small"
      })]
    });
  }

  render() {
    const {
      field,
      isEmail,
      readOnly
    } = this.props;
    return /*#__PURE__*/_jsxs(ToolFieldset, {
      children: [this.renderSupportedSourceTypesRadios(), this.renderSupportedOembedTypesSelect(), this.renderMediaBridgeProvidersOptions(), /*#__PURE__*/_jsx(ToolFormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey(`defaultLabels.${EMBED}`)
        }),
        children: /*#__PURE__*/_jsx(SidebarInput, {
          inputComponent: Embed,
          isInEmail: isEmail,
          onFieldChange: this.onDefaultChange,
          readOnly: readOnly || !field[SUPPORTED_SOURCE_TYPES][0],
          resizable: field[RESIZABLE],
          show_preview: field[SHOW_PREVIEW],
          supported_media_bridge_providers: field[SUPPORTED_MEDIA_BRIDGE_PROVIDERS],
          supported_source_types: field[SUPPORTED_SOURCE_TYPES],
          supported_oembed_types: field[SUPPORTED_OEMBED_TYPES],
          type: field.type,
          value: field[DEFAULT]
        })
      }), this.renderSizeControlToggle(), this.renderShowPreviewToggle()]
    });
  }

}

EmbedContentOptions.propTypes = {
  editField: PropTypes.func.isRequired,
  field: PropTypes.instanceOf(CustomWidgetField).isRequired,
  fieldErrors: PropTypes.object,
  isEmail: PropTypes.bool,
  readOnly: PropTypes.bool,

  /* Connect */
  fetchMediaBridgeProviders: PropTypes.func.isRequired,
  hasFetchedProviders: PropTypes.bool,
  mediaBridgeProviderOptions: PropTypes.arrayOf(PropTypes.object)
};
export default connect(mapStateToProps, mapDispatchToProps)(EmbedContentOptions);